import {
    ENDPOINTS_OAUTH,
    OAUTH_CODE_KEY,
    OAUTH_SCOPE,
    OAUTH_STATE,
} from '@core/constants/API';
import { NOTIFICATIONS_TYPE } from '@core/constants/NOTIFICATIONS';
import {
    getWesogUserInfo,
    updateWesogUserInfo,
    createWesogUserInfo,
    checkUserStatus,
    hasUserTokens,
    isUserLoggedIn,
    removeWesogUserInfo,
} from './wesog-user';
import { showNotification } from './notification-messages';
import { delayTranslate, translate as t } from '@core/i18n/window-language';
import {
    TRIGGER_LOGIN_USER_EVENT,
    TRIGGER_LOGOUT_USER_EVENT,
} from '@core/constants/OAUTH';
import { CHANGE_USER_PROCESS } from '@core/constants/USER';

/**
 * Authorize the user. Redirect to the OAuth2 server for authorization.
 * @returns {void}
 */
export const authorize = () => {
    /** @type {string} */
    const currentUri = window.location.href;
    /** @type {string} */
    const redirectUri = encodeURIComponent(currentUri);
    /** @type {string} */
    const scope = OAUTH_SCOPE;
    /** @type {string} */
    const state = OAUTH_STATE;

    /* Redirect to the OAuth2 server for authorization */
    window.location.href = `${ENDPOINTS_OAUTH.AUTH}?response_type=code&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
};

/**
 * Get the access token and the refresh token. There are 3 ways to get them:
 * - Using username and password (only for free pass user="", pass="")
 * - Using a valid authorization code (lasts a few seconds) received after calling authorize()
 * - Using a valid refresh token (lasts a few weeks)
 * @param {Object} body - The body to send to the server
 * @returns {Promise<object>} - The access token and the refresh token
 * @throws {Error} - If the response is not ok
 */
export const getTokens = async (body) => {
    try {
        /** @type {Response} */
        const response = await fetch(ENDPOINTS_OAUTH.TOKEN, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });

        if (response.ok) {
            /** @type {{ access_token: string, refresh_token: string, expires_in: number, token_type: string}} */
            const responseJson = await response.json();

            /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
            const user = getWesogUserInfo();

            /** @type {number} */
            const now = Math.floor(Date.now() / 1000);
            /** @type {number} */
            const OFFSET_MARGIN = 10;
            /** @type {number} */
            const expires_in = now + responseJson.expires_in - OFFSET_MARGIN;

            if (user) {
                user.access_token = responseJson.access_token;
                user.refresh_token = responseJson.refresh_token;
                user.expires_in = expires_in;

                updateWesogUserInfo(user);
            } else {
                createWesogUserInfo(
                    responseJson.access_token,
                    responseJson.refresh_token,
                    '',
                    expires_in,
                );
            }

            return responseJson;
        } else {
            /** @type {string} */
            const error_msg = await response.text();

            throw new Error(error_msg);
        }
    } catch (err) {
        throw new Error(err);
    }
};

/**
 * Get the access token and the refresh token using username and password.
 * @param {string} username - The username
 * @param {string} password - The password
 * @returns {Promise<void>}
 */
export const getTokensFromUsernameAndPassword = async (username, password) => {
    /** @type {URLSearchParams} */
    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('username', username);
    formData.append('password', password);

    await getTokens(formData);

    return;
};

/**
 * Add event listener to the login success event.
 * @returns {Promise<boolean | void>} - True if the login success event is added, false otherwise.
 */
export const addLoginSuccessEvent = async () => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(OAUTH_CODE_KEY)) {
        /** @type {string} */
        const authCode = urlParams.get(OAUTH_CODE_KEY);

        /** @type {URL} */
        const url = new URL(window.location.href);
        url.searchParams.delete(OAUTH_CODE_KEY);
        url.searchParams.delete('state');

        window.history.pushState({}, document.title, url.href);

        try {
            await getTokensFromAuthCode(authCode);

            await getUserInfo();

            document.dispatchEvent(new Event(TRIGGER_LOGIN_USER_EVENT));
        } catch (error) {
            delayTranslate(() => {
                showNotification(
                    t('notification.errors.getUserInfoError'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    return true;
};

/**
 * Check if the access token is valid. If not, get a new one using the refresh token.
 * @param {Object} options - The options
 * @param {number} options.extraTime - The extra time to check if the access token is valid
 * @returns {Promise<void>}
 */
export const checkValidAccessToken = async ({ extraTime = 0 } = {}) => {
    /** @type {number} */
    const now = Math.floor(Date.now() / 1000);
    /** @type {{ username: string, access_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    /** @type {boolean} */
    const isTokenValid = user.expires_in && user.expires_in > now + extraTime;

    if (!isTokenValid || !hasUserTokens()) {
        try {
            await getTokensFromRefreshToken(user.refresh_token);
        } catch (error) {
            await removeRefreshTokenAndLogoutUser();
        }
    }

    return;
};

/**
 * Remove the refresh token and logout the user.
 * @returns {Promise<void>}
 */
export const removeRefreshTokenAndLogoutUser = async () => {
    if (isUserLoggedIn()) {
        refreshTokenErrorNotificationMessage();
    }

    await getTokensFromUsernameAndPassword('', '');
    await checkUserStatus();

    setTimeout(() => {
        window.location.reload();
    }, 2000);
};

/**
 * Refresh token error.
 * @type {function}
 * @returns {void}
 * @private
 */
export const refreshTokenErrorNotificationMessage = () => {
    delayTranslate(() => {
        showNotification(
            t('notification.errors.refreshUserError'),
            NOTIFICATIONS_TYPE.ERROR,
        );
    });
    document.dispatchEvent(new CustomEvent(TRIGGER_LOGOUT_USER_EVENT));
};

/**
 * Get the access token and the refresh token using a refresh token.
 * @param {string} refresh_token - The refresh token
 * @returns {Promise<void>}
 */
export const getTokensFromRefreshToken = async (refresh_token) => {
    /** @type {URLSearchParams} */
    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refresh_token);

    await getTokens(formData);

    return;
};

/**
 * Get the access token and the refresh token using an authorization code.
 * @param {string} auth_code - The authorization code
 * @returns {Promise<void>}
 */
export const getTokensFromAuthCode = async (auth_code) => {
    /** @type {URLSearchParams} */
    const form_data = new URLSearchParams();
    form_data.append('grant_type', 'authorization_code');
    form_data.append('code', auth_code);

    await getTokens(form_data);

    return;
};

/**
 * Get the user info using the access token.
 * @returns {Promise<boolean | void>} - True if the user info is received, false otherwise.
 */
export const getUserInfo = async () => {
    /** @type {boolean} */
    let result = false;
    /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    try {
        /** @type {Response} */
        const response = await fetch(ENDPOINTS_OAUTH.USER_INFO, {
            method: 'GET',
            headers: { token: `${user.access_token}` },
        });

        if (response.ok) {
            /** @type {{ name: string, lastname: string, email: string, id: string, language: string, phone: string, pseudo: string }} */
            const responseJson = await response.json();

            user.username = responseJson.name;
            user.lastName = responseJson.lastname;
            user.email = responseJson.email;
            user.id = responseJson.id;
            user.language = responseJson.language;
            user.phone = responseJson.phone;
            user.pseudo = responseJson.pseudo;

            updateWesogUserInfo(user);
            result = true;
        } else {
            /** @type {string} */
            const error_msg = await response.text();
            console.error(error_msg);

            throw Error(error_msg);
        }
    } catch (err) {
        console.error('catch error getUserInfo', err);

        throw Error(err);
    }

    return result;
};

/**
 * Revoke the user. Remove the access token and the refresh token.
 * @returns {Promise<boolean | void>} - True if the user is revoked, false otherwise.
 */
export const revokeUser = async () => {
    /** @type {boolean} */
    let result = false;
    /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    if (!user.refresh_token) {
        delayTranslate(() => {
            showNotification(
                t('notification.errors.revokeUserError'),
                NOTIFICATIONS_TYPE.ERROR,
            );
        });

        return;
    }

    /** @type {URLSearchParams} */
    const formData = new URLSearchParams();

    formData.append('token', user.refresh_token);
    formData.append('token_type_hint', 'refresh_token');

    try {
        /** @type {Response} */
        const response = await fetch(ENDPOINTS_OAUTH.REVOKE_USER, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData,
        });

        if (response.ok) {
            removeWesogUserInfo();
            result = true;
        } else {
            /** @type {string} */
            const error_msg = await response.text();
            console.error(error_msg);

            throw Error(error_msg);
        }
    } catch (err) {
        console.error(err);
    }

    return result;
};

/**
 * Check if the user is authorized. If not, authorize the user.
 * @returns {void}
 */
export const checkAuthorize = () => {
    /** @type {string} */
    const changeUser = localStorage.getItem(CHANGE_USER_PROCESS);

    if (changeUser && changeUser === 'true') {
        localStorage.removeItem(CHANGE_USER_PROCESS);
        authorize();
    }
};
